import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services';
import { AlertCircle, Loader2 } from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────
const COUNTRY_CODES = [
  { code: 'VN', dial: '+84',  flag: '🇻🇳' },
  { code: 'SG', dial: '+65',  flag: '🇸🇬' },
  { code: 'MY', dial: '+60',  flag: '🇲🇾' },
  { code: 'TH', dial: '+66',  flag: '🇹🇭' },
  { code: 'PH', dial: '+63',  flag: '🇵🇭' },
  { code: 'ID', dial: '+62',  flag: '🇮🇩' },
  { code: 'JP', dial: '+81',  flag: '🇯🇵' },
  { code: 'KR', dial: '+82',  flag: '🇰🇷' },
  { code: 'CN', dial: '+86',  flag: '🇨🇳' },
  { code: 'HK', dial: '+852', flag: '🇭🇰' },
  { code: 'US', dial: '+1',   flag: '🇺🇸' },
];

// T-shirt sizes per spec (XS → XXXXL)
const SHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'XXXXL'];

// Grade scales per spec
const GRADE_SCALES = {
  letter: {
    label: 'A / ABCD',
    options: ['A+', 'A', 'B+', 'B', 'C+', 'C', 'D+', 'D', 'F'],
  },
  gpa4: {
    label: '4.0 GPA',
    options: ['4.0', '3.7', '3.3', '3.0', '2.7', '2.3', '2.0', '1.7', '1.3', '1.0', '0.0'],
  },
  gpa10: {
    label: '10-point',
    options: ['10', '9', '8', '7', '6', '5', '4', '3', '2', '1'],
  },
};

const detectGradeScale = (val) => {
  if (!val) return 'letter';
  if (GRADE_SCALES.letter.options.includes(val)) return 'letter';
  if (GRADE_SCALES.gpa4.options.includes(val))   return 'gpa4';
  if (GRADE_SCALES.gpa10.options.includes(val))  return 'gpa10';
  return 'letter';
};

const CAREER_OPTIONS = [
  'Agriculture, Forestry & Fishing', 'Architecture & Design', 'Banking & Finance',
  'Business Owner / Entrepreneur', 'Civil Service / Government', 'Construction & Engineering',
  'Consulting & Advisory', 'Education — Teacher / Lecturer', 'Healthcare — Doctor / Physician',
  'Healthcare — Nurse / Medical Staff', 'Healthcare — Pharmacist', 'Healthcare — Dentist / Orthodontist',
  'Hospitality & Tourism', 'Human Resources', 'Information Technology', 'Insurance',
  'Journalism & Media', 'Law — Lawyer / Legal Professional', 'Logistics & Supply Chain',
  'Manufacturing & Production', 'Marketing & Advertising', 'Military & Defense', 'Non-profit / NGO',
  'Real Estate', 'Research & Academia', 'Retail & Trade', 'Sales & Business Development',
  'Social Work & Community Services', 'Sports & Fitness', 'Transportation & Logistics',
  'Homemaker / Stay-at-home parent', 'Retired', 'Other',
];

// ─── Shared style helpers ─────────────────────────────────────────────────────
const inp = 'w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none transition-all';
const lbl = 'block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5';
const req = <span className="text-rose-500 ml-0.5">*</span>;

// ─── Phone row ────────────────────────────────────────────────────────────────
function PhoneRow({ dial, phone, onDial, onPhone, helperText, testPrefix = 'ob' }) {
  return (
    <div>
      <div className="flex gap-2">
        <select
          data-testid={`${testPrefix}-dial`}
          className="w-28 shrink-0 rounded-2xl border border-slate-200 bg-slate-50 px-2 py-2.5 text-sm text-slate-900 focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none transition-all"
          value={dial}
          onChange={onDial}
        >
          {COUNTRY_CODES.map(c => (
            <option key={c.code} value={c.dial}>{c.flag} {c.dial}</option>
          ))}
        </select>
        <input
          data-testid={`${testPrefix}-phone`}
          type="tel"
          className={`${inp} flex-1`}
          value={phone}
          onChange={onPhone}
          placeholder="Phone number"
          required
        />
      </div>
      {helperText && (
        <p className="text-xs text-slate-400 mt-1.5 flex items-start gap-1">
          <span className="flex-shrink-0">ℹ️</span>
          {helperText}
        </p>
      )}
    </div>
  );
}

// ─── Sex radio group ──────────────────────────────────────────────────────────
function SexRadio({ value, onChange, testPrefix = 'ob' }) {
  return (
    <div className="flex gap-3">
      {[['male', 'Male', '👨'], ['female', 'Female', '👩']].map(([val, label, emoji]) => (
        <button
          key={val}
          type="button"
          data-testid={`${testPrefix}-sex-${val}`}
          onClick={() => onChange(val)}
          className={`flex-1 flex flex-col items-center gap-1 py-3 rounded-2xl border-2 text-sm font-semibold transition-all duration-150
            ${value === val
              ? 'border-sky-400 bg-sky-50 text-sky-700'
              : 'border-slate-200 bg-slate-50 text-slate-500 hover:border-sky-200'}`}
        >
          <span className="text-2xl">{emoji}</span>
          {label}
        </button>
      ))}
    </div>
  );
}

// ─── T-shirt size grid ────────────────────────────────────────────────────────
function TshirtGrid({ value, onChange, testPrefix = 'ob' }) {
  return (
    <div className="flex flex-wrap gap-2">
      {SHIRT_SIZES.map(size => (
        <button
          key={size}
          type="button"
          data-testid={`${testPrefix}-tshirt-${size}`}
          onClick={() => onChange(size)}
          className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-all border-2
            ${value === size
              ? 'bg-sky-500 text-white border-sky-500 shadow-sm'
              : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-sky-300 hover:text-sky-600'}`}
        >
          {size}
        </button>
      ))}
    </div>
  );
}

// ─── Grade field — 3-scale selector ──────────────────────────────────────────
function GradeField({ value, onChange, testPrefix = 'ob' }) {
  const [scale, setScale] = useState(() => detectGradeScale(value));

  const handleScaleChange = (newScale) => {
    setScale(newScale);
    onChange(''); // reset value when switching scale
  };

  return (
    <div>
      {/* Scale tabs */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl mb-2">
        {Object.entries(GRADE_SCALES).map(([key, { label }]) => (
          <button
            key={key}
            type="button"
            data-testid={`${testPrefix}-grade-scale-${key}`}
            onClick={() => handleScaleChange(key)}
            className={`flex-1 py-1.5 px-2 rounded-lg text-xs font-semibold transition-all duration-150
              ${scale === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            {label}
          </button>
        ))}
      </div>
      <select
        data-testid={`${testPrefix}-grade`}
        className={inp}
        value={value}
        onChange={e => onChange(e.target.value)}
        required
      >
        <option value="">Select grade</option>
        {GRADE_SCALES[scale].options.map(g => <option key={g} value={g}>{g}</option>)}
      </select>
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────
export default function OnboardingModal() {
  // onboardingRole is advisory (from profile.family_role), used only for UI routing
  const { onboardingRole, userName, userEmail, setOnboardingComplete, fetchUserProfile } = useAuth();
  const isChild = onboardingRole === 'child';

  const [form, setForm] = useState({
    name:         userName || '',
    sex:          '',
    dob:          '',   // YYYY-MM-DD — required for everyone
    address:      '',   // free text, max 255
    dial:         '+84',
    phone:        '',
    // Child-specific (required)
    tshirt_size:  '',
    school_name:  '',
    grade:        '',
    // Parent optional
    career:       '',
    company_name: '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  // Pre-fill from existing profile snapshot
  useEffect(() => {
    authService.getProfile().then(p => {
      if (!p) return;
      const dialMatch = COUNTRY_CODES.find(c => (p.phone || '').startsWith(c.dial));
      const dialCode  = dialMatch ? dialMatch.dial : '+84';
      const phoneNum  = dialMatch ? (p.phone || '').slice(dialCode.length).trim() : (p.phone || '');

      setForm(prev => ({
        ...prev,
        name:         p.name && p.name !== userEmail ? p.name : prev.name,
        sex:          p.sex          || '',
        dob:          p.dob          ? p.dob.split('T')[0] : '',
        address:      p.address      || '',
        dial:         dialCode,
        phone:        phoneNum,
        tshirt_size:  p.tshirt_size  || '',
        school_name:  p.school_name  || '',
        grade:        p.grade        || '',
        career:       p.career       || '',
        company_name: p.company_name || '',
      }));
    }).catch(() => {});
  }, []); // eslint-disable-line

  const set = k => v =>
    setForm(prev => ({ ...prev, [k]: typeof v === 'string' ? v : v.target?.value ?? v }));

  const isValid = () => {
    if (!form.name || !form.sex || !form.dob || !form.phone || !form.address) return false;
    if (isChild && (!form.tshirt_size || !form.school_name || !form.grade)) return false;
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid()) { setError('Please fill in all required fields.'); return; }
    setSaving(true);
    setError('');
    try {
      // Build payload — ❌ NEVER send `age` (calculated from DOB on backend)
      const payload = {
        name:    form.name,
        sex:     form.sex,
        dob:     form.dob,
        phone:   `${form.dial} ${form.phone}`.trim(),
        address: form.address,
      };
      if (isChild) {
        payload.tshirt_size = form.tshirt_size;
        payload.school_name = form.school_name;
        payload.grade       = form.grade;
      } else {
        if (form.career)       payload.career       = form.career;
        if (form.company_name) payload.company_name = form.company_name;
      }
      await authService.updateProfile(payload);
      // Refresh profile so tshirtSize + onboarding states update in AuthContext
      await fetchUserProfile();
      setOnboardingComplete();
    } catch (err) {
      setError(
        err?.response?.data?.error_message ||
        err?.response?.data?.message ||
        'Failed to save. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm">
      <div
        data-testid="onboarding-modal"
        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white/95 backdrop-blur-sm px-8 pt-8 pb-5 border-b border-slate-100 z-10">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-3 text-2xl
            ${isChild ? 'bg-violet-100' : 'bg-sky-100'}`}>
            {isChild ? '🎒' : '👋'}
          </div>
          <h2 className="text-2xl font-bold text-slate-900 font-outfit">Complete Your Profile</h2>
          <p className="text-slate-500 text-sm mt-1">
            {isChild
              ? 'Fill in the details below to unlock your full experience.'
              : 'A few details to get started — takes less than a minute.'}
          </p>
          <div className="mt-3 text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 flex items-center gap-2">
            <span className="flex-shrink-0">⚠️</span>
            This information is required every sign-in until completed.
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-8 py-6 space-y-5">

          {/* Full Name */}
          <div>
            <label className={lbl}>Full Name {req}</label>
            <input
              data-testid="ob-name"
              className={inp}
              value={form.name}
              onChange={set('name')}
              placeholder="Your full name"
              required
            />
          </div>

          {/* Sex */}
          <div>
            <label className={lbl}>Gender {req}</label>
            <SexRadio value={form.sex} onChange={set('sex')} testPrefix="ob" />
          </div>

          {/* Date of Birth — required for EVERYONE */}
          <div>
            <label className={lbl}>Date of Birth {req}</label>
            <input
              data-testid="ob-dob"
              type="date"
              className={inp}
              value={form.dob}
              onChange={set('dob')}
              max={new Date().toISOString().split('T')[0]}
              required
            />
          </div>

          {/* Phone */}
          <div>
            <label className={lbl}>Phone {req}</label>
            <PhoneRow
              dial={form.dial}
              phone={form.phone}
              onDial={set('dial')}
              onPhone={set('phone')}
              testPrefix="ob"
              helperText={isChild
                ? "You can input your parent's phone number here if you don't own one."
                : undefined}
            />
          </div>

          {/* Address — single free-text field */}
          <div>
            <label className={lbl}>Address {req}</label>
            <input
              data-testid="ob-address"
              className={inp}
              value={form.address}
              onChange={set('address')}
              placeholder="Street, District, City / Province"
              maxLength={255}
              required
            />
          </div>

          {/* ── CHILD-SPECIFIC FIELDS ── */}
          {isChild && (
            <div className="space-y-5 border-t border-slate-100 pt-5">
              <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold">Academic Info</p>

              {/* T-Shirt Size */}
              <div>
                <label className={lbl}>T-Shirt Size {req}</label>
                <TshirtGrid value={form.tshirt_size} onChange={set('tshirt_size')} testPrefix="ob" />
              </div>

              {/* School */}
              <div>
                <label className={lbl}>School Name {req}</label>
                <input
                  data-testid="ob-school"
                  className={inp}
                  value={form.school_name}
                  onChange={set('school_name')}
                  placeholder="Your school name"
                  maxLength={255}
                  required
                />
              </div>

              {/* Grade with 3-scale selector */}
              <div>
                <label className={lbl}>Grade {req}</label>
                <GradeField
                  value={form.grade}
                  onChange={set('grade')}
                  testPrefix="ob"
                />
              </div>
            </div>
          )}

          {/* ── PARENT OPTIONAL FIELDS ── */}
          {!isChild && (
            <div className="space-y-4 border-t border-slate-100 pt-5">
              <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold">Professional Info
                <span className="ml-1 font-normal normal-case tracking-normal">(optional)</span>
              </p>
              <div>
                <label className={lbl}>Occupation</label>
                <select data-testid="ob-career" className={inp} value={form.career} onChange={set('career')}>
                  <option value="">Select your career</option>
                  {CAREER_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>Workplace</label>
                <input
                  data-testid="ob-company"
                  className={inp}
                  value={form.company_name}
                  onChange={set('company_name')}
                  placeholder="Where you work"
                  maxLength={255}
                />
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div data-testid="ob-error" className="p-3 bg-red-50 border border-red-200 rounded-2xl text-red-600 text-sm flex items-start gap-2">
              <AlertCircle size={15} className="flex-shrink-0 mt-0.5" /> {error}
            </div>
          )}

          {/* Submit */}
          <button
            data-testid="ob-submit"
            type="submit"
            disabled={!isValid() || saving}
            className="w-full bg-sky-500 hover:bg-sky-600 active:bg-sky-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white rounded-2xl font-semibold py-3.5 transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center gap-2"
          >
            {saving
              ? <><Loader2 size={16} className="animate-spin" /> Saving…</>
              : 'Complete Profile'}
          </button>
        </form>
      </div>
    </div>
  );
}
